import { useT } from "../../../shared/i18n/i18n";
import { useCallback, useMemo, useRef, useState } from "react";
import type { BoardItemView } from "../../../entities/board/model/use-boards";
import type { WorkObjectFieldDefinition } from "../../../entities/work-object-field/model/use-work-object-fields";
import type { UpdateWorkObjectFieldValuesInput } from "../../../entities/work-object/model/use-work-objects";
import { useBoardTimelineItems, type BoardTimelineRange } from "../model/use-board-timeline-items";
import { parseTimelineDate, toTimelineDateInput, type TimelineDateSpan, type TimelineScale } from "../model/timeline";
import { Timeline, type TimelineGroup, type TimelineRowMetaPart, type TimelineVisibleRange } from "./timeline";

export type TimelineGroupingDefinition = {
  color: string | null;
  key: string;
  label: string;
  value: string;
};

export type TimelineGrouping = {
  /** Ordered group definitions; every definition renders even when empty. */
  definitions: TimelineGroupingDefinition[];
  /** Field the view groups by (the board status field for status grouping). */
  fieldId: string;
  isStatus: boolean;
  /** Whether moving items between groups (and creating into them) is allowed. */
  writable: boolean;
};

export function BoardTimelineView({
  boardId,
  boardViewId,
  fields,
  grouping,
  includeClosedItems,
  itemSummary,
  onChangeItemStatus,
  onCreateItem,
  onOpenItem,
  onUpdateFieldValues,
  queryKey,
  sprintScope = null,
  timelineDateFieldId,
}: {
  boardId: string;
  boardViewId: string;
  fields: WorkObjectFieldDefinition[];
  grouping?: TimelineGrouping;
  includeClosedItems: boolean;
  itemSummary?: (item: BoardItemView) => TimelineRowMetaPart[] | null;
  onChangeItemStatus?: (item: BoardItemView, boardStatusId: string) => Promise<void>;
  onCreateItem?: (input: { boardStatusId?: string; title: string; values: Array<{ fieldDefinitionId: string; value: string | string[] }> }) => Promise<void>;
  onOpenItem: (item: BoardItemView) => void;
  onUpdateFieldValues: (input: UpdateWorkObjectFieldValuesInput) => Promise<void>;
  queryKey?: string;
  sprintScope?: string | null;
  timelineDateFieldId: string | null;
}) {
  const t = useT();
  const [range, setRange] = useState<BoardTimelineRange | null>(null);
  const [scale, setScale] = useState<TimelineScale>(() => readStoredTimelineScale(boardViewId));
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<string[]>(() => readStoredCollapsedGroups(boardViewId));
  const updateQueueRef = useRef(new Map<string, Promise<void>>());
  const dateField = useMemo(() => resolveTimelineDateField(fields, timelineDateFieldId), [fields, timelineDateFieldId]);
  const { applyItemFieldValuesOptimistic, applyItemStatusOptimistic, error, invalidate, isLoading, items } = useBoardTimelineItems({
    boardId,
    boardViewId,
    dateFieldId: dateField?.id ?? null,
    includeClosedItems,
    queryKey,
    range: dateField ? range : null,
    sprintScope,
  });

  const timelineGroups = useMemo<TimelineGroup<BoardItemView>[]>(() => {
    if (!dateField) return [];
    const rows = items.flatMap((item) => {
      const span = spanForField(item, dateField);
      if (!span) return [];
      return [{
        canResize: dateField.type === "date_range",
        color: item.status.color,
        end: span.end,
        id: item.id,
        item,
        label: item.workObject.title,
        meta: itemSummary?.(item) ?? null,
        start: span.start,
      }];
    });
    if (!grouping) {
      return rows.length > 0 ? [{ color: null, items: rows, key: "scheduled", label: t("board.scheduledItems") }] : [];
    }
    const buckets = new Map<string, TimelineGroup<BoardItemView>>();
    for (const definition of grouping.definitions) {
      buckets.set(definition.value, { color: definition.color, items: [], key: definition.key, label: definition.label });
    }
    for (const row of rows) {
      const value = groupValueForItem(row.item, grouping);
      let bucket = buckets.get(value);
      if (!bucket) {
        // Values outside the definitions (stale options, computed fields)
        // still get a section, they are just not drop targets.
        bucket = { color: null, items: [], key: `value:${value}`, label: value === "" ? "No value" : value };
        buckets.set(value, bucket);
      }
      bucket.items.push(row);
    }
    return [...buckets.values()];
  }, [dateField, grouping, items, itemSummary]);

  const toggleGroupCollapse = useCallback((groupKey: string) => {
    setCollapsedGroupKeys((current) => {
      const next = current.includes(groupKey) ? current.filter((key) => key !== groupKey) : [...current, groupKey];
      storeCollapsedGroups(boardViewId, next);
      return next;
    });
  }, [boardViewId]);

  const handleRangeChange = useCallback((nextRange: TimelineVisibleRange) => {
    setRange((current) => current?.startDate === nextRange.startDate && current.endDate === nextRange.endDate ? current : nextRange);
  }, []);

  const handleScaleChange = useCallback((nextScale: TimelineScale) => {
    setScale(nextScale);
    storeTimelineScale(boardViewId, nextScale);
  }, [boardViewId]);

  // Saves for the same work object run one after another, so rapid
  // consecutive drags (or a combined dates+group drop) cannot land out of
  // order on the server.
  const enqueueItemUpdate = useCallback((workObjectId: string, task: () => Promise<void>) => {
    const previousUpdate = updateQueueRef.current.get(workObjectId) ?? Promise.resolve();
    const update = previousUpdate.catch(() => undefined).then(task);
    updateQueueRef.current.set(workObjectId, update);
    const cleanup = () => {
      if (updateQueueRef.current.get(workObjectId) === update) updateQueueRef.current.delete(workObjectId);
    };
    void update.then(cleanup, cleanup);
    return update;
  }, []);

  const handleChangeDates = useCallback(async (item: BoardItemView, nextSpan: TimelineDateSpan) => {
    if (!dateField) return;
    const startValue = toTimelineDateInput(nextSpan.start);
    const endValue = toTimelineDateInput(nextSpan.end);
    const values = [{
      fieldDefinitionId: dateField.id,
      value: dateField.type === "date_range" ? [startValue, endValue] : startValue,
    }];
    const workObjectId = item.workObject.id;
    // The optimistic patch applies before the queue so a drop that also
    // changes the group repaints once, with both changes.
    const rollback = applyItemFieldValuesOptimistic(workObjectId, values);
    await enqueueItemUpdate(workObjectId, async () => {
      try {
        await onUpdateFieldValues({ values, workObjectId });
      } catch (error) {
        rollback();
        throw error;
      }
    });
  }, [applyItemFieldValuesOptimistic, dateField, enqueueItemUpdate, onUpdateFieldValues]);

  const handleCreateItem = useMemo(() => {
    if (!onCreateItem || !dateField) return undefined;
    return async ({ groupKey, span, title }: { groupKey: string | null; span: TimelineDateSpan; title: string }) => {
      const startValue = toTimelineDateInput(span.start);
      const endValue = toTimelineDateInput(span.end);
      const values: Array<{ fieldDefinitionId: string; value: string | string[] }> = [{
        fieldDefinitionId: dateField.id,
        value: dateField.type === "date_range" ? [startValue, endValue] : startValue,
      }];
      let boardStatusId: string | undefined;
      // Drawing inside a group's band assigns that group's value to the new item.
      const definition = grouping?.writable && groupKey ? grouping.definitions.find((candidate) => candidate.key === groupKey) : undefined;
      if (definition && grouping) {
        if (grouping.isStatus) boardStatusId = definition.value || undefined;
        else values.push({ fieldDefinitionId: grouping.fieldId, value: definition.value });
      }
      await onCreateItem({ boardStatusId, title, values });
      await invalidate();
    };
  }, [dateField, grouping, invalidate, onCreateItem]);

  const handleChangeItemGroup = useMemo(() => {
    if (!grouping?.writable) return undefined;
    return async (item: BoardItemView, groupKey: string) => {
      const definition = grouping.definitions.find((candidate) => candidate.key === groupKey);
      if (!definition) return;
      // The optimistic patch applies synchronously at the drop, in the same
      // frame as the dates patch; the mutation itself joins the per-object
      // queue so it runs after an in-flight dates save, and the refetch below
      // never reads the server mid-way through the combined change.
      if (grouping.isStatus) {
        if (!onChangeItemStatus || !definition.value || definition.value === item.status.id) return;
        const rollback = applyItemStatusOptimistic(item.workObject.id, { color: definition.color ?? undefined, id: definition.value, name: definition.label });
        await enqueueItemUpdate(item.workObject.id, async () => {
          try {
            await onChangeItemStatus(item, definition.value);
          } catch (error) {
            rollback();
            throw error;
          }
        });
        await invalidate();
        return;
      }
      const values = [{ fieldDefinitionId: grouping.fieldId, value: definition.value }];
      const rollback = applyItemFieldValuesOptimistic(item.workObject.id, values);
      await enqueueItemUpdate(item.workObject.id, async () => {
        try {
          await onUpdateFieldValues({ values, workObjectId: item.workObject.id });
        } catch (error) {
          rollback();
          throw error;
        }
      });
    };
  }, [applyItemFieldValuesOptimistic, applyItemStatusOptimistic, enqueueItemUpdate, grouping, invalidate, onChangeItemStatus, onUpdateFieldValues]);

  const dateFieldCount = useMemo(() => fields.filter(isDateField).length, [fields]);

  return (
    <Timeline
      canCreateRange={dateField?.type === "date_range"}
      collapsedGroupKeys={grouping ? collapsedGroupKeys : undefined}
      emptyMessage={
        error
          ?? (dateFieldCount === 0
            ? "Add a date field to place items on the timeline."
            : !dateField
              ? "Choose a date field in view settings."
              : handleCreateItem
                ? t("timeline.emptyDraggable")
                : t("timeline.emptyReadOnly"))
      }
      grouped={Boolean(grouping)}
      groups={timelineGroups}
      loading={isLoading}
      onChangeItemDates={handleChangeDates}
      onChangeItemGroup={handleChangeItemGroup}
      onCreateItem={handleCreateItem}
      onItemClick={onOpenItem}
      onScaleChange={handleScaleChange}
      onToggleGroupCollapse={grouping ? toggleGroupCollapse : undefined}
      onVisibleRangeChange={handleRangeChange}
      scale={scale}
    />
  );
}

export function resolveTimelineDateField(fields: WorkObjectFieldDefinition[], selectedDateFieldId: string | null) {
  const dateFields = fields.filter(isDateField);
  if (selectedDateFieldId) {
    return dateFields.find((field) => field.id === selectedDateFieldId) ?? null;
  }
  return dateFields.find((field) => field.type === "date_range") ?? dateFields[0] ?? null;
}

function isDateField(field: WorkObjectFieldDefinition) {
  return field.type === "date" || field.type === "date_range";
}

function spanForField(item: BoardItemView, field: WorkObjectFieldDefinition): TimelineDateSpan | null {
  const values = fieldValues(item, field.id);
  const start = parseTimelineDate(values[0]);
  const end = parseTimelineDate(field.type === "date_range" ? values[1] ?? values[0] : values[0]);
  return start && end ? { end: end < start ? start : end, start } : null;
}

function fieldValues(item: BoardItemView, fieldId: string) {
  const value = item.fieldValues.find((fieldValue) => fieldValue.fieldDefinitionId === fieldId)?.value;
  return Array.isArray(value) ? value.map(String).filter(Boolean) : typeof value === "string" && value ? [value] : [];
}

function groupValueForItem(item: BoardItemView, grouping: TimelineGrouping) {
  if (grouping.isStatus) {
    return item.status.id;
  }
  const value = item.fieldValues.find((fieldValue) => fieldValue.fieldDefinitionId === grouping.fieldId)?.value;
  return (Array.isArray(value) ? value[0] : value) ?? "";
}

function timelineScaleStorageKey(boardViewId: string) {
  return `impacto.timeline.scale.${boardViewId}`;
}

function collapsedGroupsStorageKey(boardViewId: string) {
  return `impacto.timeline.collapsed.${boardViewId}`;
}

function readStoredCollapsedGroups(boardViewId: string): string[] {
  try {
    const stored = JSON.parse(localStorage.getItem(collapsedGroupsStorageKey(boardViewId)) ?? "[]") as unknown;
    return Array.isArray(stored) ? stored.filter((key): key is string => typeof key === "string") : [];
  } catch {
    return [];
  }
}

function storeCollapsedGroups(boardViewId: string, groupKeys: string[]) {
  try {
    localStorage.setItem(collapsedGroupsStorageKey(boardViewId), JSON.stringify(groupKeys));
  } catch {
    // Collapse state is a convenience; ignore storage failures.
  }
}

function readStoredTimelineScale(boardViewId: string): TimelineScale {
  try {
    const stored = localStorage.getItem(timelineScaleStorageKey(boardViewId));
    return stored === "days" || stored === "weeks" || stored === "months" ? stored : "days";
  } catch {
    return "days";
  }
}

function storeTimelineScale(boardViewId: string, scale: TimelineScale) {
  try {
    localStorage.setItem(timelineScaleStorageKey(boardViewId), scale);
  } catch {
    // Persisting the scale is a convenience; ignore storage failures.
  }
}
